import { cn } from '@heidi/utils';
import type { ComponentType, ReactNode } from 'react';
import type { MarkdownComponents } from '../markdown/markdown-components';
import type { ProseDensity } from '../streamdown/response-prose';
import { responseBodyTextClass } from '../streamdown/response-type';

/**
 * Shared marker class for "callout" blocks in chat responses
 * (currently `KeyPointsBlock` and `KeySafetyNotes`).
 *
 * Declared in `response-prose` because the prose wrapper is what scopes the
 * callout's outer gutter and stops the body rhythm leaking into the card; it is
 * re-exported here so the two callout components keep importing it from the
 * module that owns their bullet rendering.
 *
 * Single marker (instead of per-block `.keypoints-block` / `.key-safety-notes-block`)
 * keeps the two blocks visually consistent by construction: any new rule has to
 * apply to both.
 */
export { CALLOUT_BLOCK_MARKER_CLASS } from '../streamdown/response-prose';

export type CalloutVariant = 'in-session' | 'out-of-session';

/**
 * The callout variant is the prose density under an older name — `in-session`
 * was always standing in for "narrow reference panel". Both names survive
 * because the callout components are a public-ish surface with their own
 * consumers, but there is one scale underneath, resolved here.
 *
 * Density is a property of the surface, not of the session. It has to be
 * whatever the prose beside the callout is already set to. A host that knows
 * its own prose size should pass `density` rather than let one be guessed
 * from the variant.
 */
export type CalloutDensity = ProseDensity;

export const calloutVariantToDensity = (variant: CalloutVariant): ProseDensity =>
  variant === 'in-session' ? 'compact' : 'comfortable';

/** The inverse, for a host that knows its density and renders a callout inside it. */
export const densityToCalloutVariant = (density: ProseDensity): CalloutVariant =>
  density === 'compact' ? 'in-session' : 'out-of-session';

/**
 * How tightly the surface hosting a callout sets its type. The in-session panel
 * is narrow and sits beside a transcript, so it reads at 14px; a full-width
 * out-of-session answer reads at 16px.
 */
export const getCalloutDensity = (variant: CalloutVariant): CalloutDensity =>
  calloutVariantToDensity(variant);

/**
 * Header type per density, named as design-system Typography variants.
 *
 * Compact is p2 (14px), comfortable is p1 (16px). `header` is the design
 * system's `*Bold` step (`font-medium`) — a callout label is a label, not a
 * heading, and Exposure stays reserved for KeyPoints. Body copy in the bullet
 * list does not read this table: it uses `responseBodyTextClass` so it cannot
 * drift from the prose around the card.
 */
export const CALLOUT_DENSITY_TYPOGRAPHY = {
  compact: { body: 'p2', header: 'p2Bold' },
  comfortable: { body: 'p', header: 'pBold' },
} as const satisfies Record<CalloutDensity, { body: string; header: string }>;

/** Icon size that optically matches the header type at each density. */
export const CALLOUT_DENSITY_ICON_SIZE = {
  compact: 14,
  comfortable: 16,
} as const satisfies Record<CalloutDensity, number>;

/** Handler shape Streamdown passes to a custom `<a>` renderer. */
type LinkComponent = ComponentType<{ children?: ReactNode; href?: string }>;

/** Handler shape Streamdown passes to a custom `<cite-badge>` renderer.
 *  Each callout supplies its own so it can pick the right `surfaceBlock`,
 *  partner fill, and surface theme without leaking that knowledge here. */
type CiteBadgeComponent = ComponentType<{ 'data-idx'?: string }>;

interface GetCalloutBulletComponentsOptions {
  /** The hosting surface's density. Callers resolve it from their own prose
   *  size — see `CalloutDensity` — rather than passing a session flag. */
  density: CalloutDensity;
  /** Optional `<a>` handler. Only KeyPointsBlock currently embeds links. */
  linkComponent?: LinkComponent;
  /** Optional `<cite-badge>` handler. Both callouts use this when a citation
   *  map is provided; the handler differs per surface. */
  citeBadgeComponent?: CiteBadgeComponent;
  /**
   * `'none'` drops the disc marker and the hanging indent. A list of one is
   * not a list — a single bullet under a header reads as a stray mark rather
   * than as enumeration, so the caller passes this when the whole callout
   * comes to one item. Defaults to `'disc'`.
   */
  listStyle?: 'disc' | 'none';
}

/**
 * Shared markdown component handlers for the bullet list inside a callout
 * block. Both `KeyPointsBlock` and `KeySafetyNotes` consume this single
 * factory so the two callouts render with identical typography, weight, and
 * bullet rhythm — diverging only on color (inherited from the wrapping card
 * via `text-*` on a parent div) and the optional `a` / `cite-badge`
 * handlers, which the consumer plugs in via the options arg.
 *
 * What's encoded here:
 * - Body size and leading match the surrounding prose exactly, by reading the
 *   SAME density table the prose map reads rather than restating a size here.
 *   A callout that sized itself independently is how these drifted before: the
 *   card is meant to highlight its content, so text smaller than the body
 *   around it reads as less important than the prose it interrupts.
 * - Density-aware indent: compact `ps-5`, comfortable `ps-6` (logical-direction,
 *   RTL-safe). Dropped entirely when `listStyle` is `'none'`.
 * - Constant leading: `leading-normal` (1.5) inline. OOS forces this down
 *   to 1.45 via `outOfSessionSpacing`'s `[&_.callout-block_li]:!leading-[1.45]`
 *   rule so callout density matches OOS body prose.
 * - Bullet rhythm driven by the `<ul>` itself, not by per-`<li>` margins:
 *   - `[&>li:first-child]:!mt-0` flushes the first bullet against the card
 *     header (otherwise the body's `li` top margin leaks in and adds a 4px gap
 *     inside the card).
 *   - `[&>li:not(:first-child)]:!mt-2` gives 8px between bullets, one step
 *     looser than the body's `mt-1` since callout bullets carry more weight
 *     per item.
 *   Both selectors compile to specificity (0,2,1), which beats the body's
 *   (0,1,1) deterministically.
 * - `font-medium` on `<strong>` matches the broader prose `<strong>` so
 *   inline bolding reads consistently across every chat surface.
 */
export const getCalloutBulletComponents = ({
  density,
  linkComponent,
  citeBadgeComponent,
  listStyle = 'disc',
}: GetCalloutBulletComponentsOptions): MarkdownComponents => {
  const textClass = responseBodyTextClass(density);
  const listStyleClass = listStyle === 'none' ? 'list-none' : 'list-disc';
  const indentClass =
    listStyle === 'none' ? 'ps-0' : density === 'comfortable' ? 'ps-6' : 'ps-5';
  const leadingClass = 'leading-normal';

  // `dir="auto"` on the block-level handlers for the same reason as the main
  // markdown component sets (see markdown-components.tsx): callout content is
  // unlocalized LLM markdown, so each block resolves its own bidi direction
  // instead of inheriting the RTL locale direction (which mirrors/relocates
  // neutral characters like `≥` and trailing periods). No-op in LTR locales.
  const components: Record<string, unknown> = {
    ul: ({ children }: { children?: ReactNode }) => (
      <ul
        dir="auto"
        className={cn(
          listStyleClass,
          'font-normal',
          '[&>li:first-child]:!mt-0 [&>li:not(:first-child)]:!mt-2',
          indentClass,
          textClass,
          leadingClass
        )}
      >
        {children}
      </ul>
    ),
    li: ({ children }: { children?: ReactNode }) => (
      <li dir="auto" className={cn('font-normal', textClass, leadingClass)}>
        {children}
      </li>
    ),
    p: ({ children }: { children?: ReactNode }) => (
      <span dir="auto" className={cn('font-normal', textClass, leadingClass)}>
        {children}
      </span>
    ),
    strong: ({ children }: { children?: ReactNode }) => (
      <strong className="font-medium">{children}</strong>
    ),
  };

  if (linkComponent) {
    components.a = linkComponent;
  }
  if (citeBadgeComponent) {
    components['cite-badge'] = citeBadgeComponent;
  }

  return components as MarkdownComponents;
};
